import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { 
  DollarSign,
  Users,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Search,
  Eye,
  Plus,
  Settings,
  GitBranch,
  Clock,
  FileText,
  RotateCw as Sync,
  AlertTriangle,
  BarChart3,
  Package,
  CreditCard,
  Shield,
  Edit,
  Trash2,
  Wallet,
  Grid3X3,
  DollarSign as PricingIcon
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AdminWalletManagement from './AdminWalletManagement';
import PlanEditor from './PlanEditor';
import FeatureMatrix from './FeatureMatrix';
import PlanVersionHistory from './PlanVersionHistory';
import PlanComparison from './PlanComparison';
import PlanViewer from './PlanViewer';
import AdvancedPlanSearch from './AdvancedPlanSearch';
import BulkPlanOperations from './BulkPlanOperations';
import PricingControls from './PricingControls';
import EnhancedApprovals from './EnhancedApprovals';
import EnhancedStripeSync from './EnhancedStripeSync';
import EnhancedWalletManagement from './EnhancedWalletManagement';
import { PerformanceMetrics } from '../ui/SystemHealth';
import { AlertManager } from '../ui/AlertManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const MonetizationStudio = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [plans, setPlans] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [showPlanEditor, setShowPlanEditor] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showPlanComparison, setShowPlanComparison] = useState(false);
  const [showPlanViewer, setShowPlanViewer] = useState(false);
  const [versionHistoryPlanId, setVersionHistoryPlanId] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [viewingPlan, setViewingPlan] = useState(null);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  // Use periodic API polling instead of WebSocket for reliability
  useEffect(() => {
    fetchOverviewData();
    fetchSystemAlerts();
    
    // Set up periodic refresh (every 60 seconds)
    const refreshInterval = setInterval(() => {
      if (autoRefreshEnabled && activeTab === 'overview') {
        fetchOverviewData();
        fetchSystemAlerts();
      }
    }, 60000); // 60 seconds
    
    return () => clearInterval(refreshInterval);
  }, [autoRefreshEnabled, activeTab]);

  const fetchSystemAlerts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/system/alerts`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle both array and object responses
        const alertsArray = Array.isArray(data) ? data : (data.alerts || []);
        setAlerts(alertsArray);
      } else {
        console.error('Failed to fetch alerts:', response.status);
        // Don't show error to user for alerts fetch failure
        setAlerts([]);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
      // Silently fail and show no alerts
      setAlerts([]);
    }
  };

  const handleResolveAlert = async (alertId) => {
    const response = await fetch(`${BACKEND_URL}/api/admin/system/alerts/${alertId}/resolve`, {
      method: 'POST',
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to resolve alert');
    }
    
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  };

  useEffect(() => {
    if (activeTab === 'plans') {
      fetchPlansData();
    } else if (activeTab === 'approvals') {
      fetchApprovalsData();
    } else if (activeTab === 'sync') {
      fetchSyncData();
    }
  }, [activeTab]);

  const handleCreatePlan = () => {
    setEditingPlanId(null);
    setShowPlanEditor(true);
  };

  const handleEditPlan = (planId) => {
    setEditingPlanId(planId);
    setShowPlanEditor(true);
  };

  const handleViewPlan = (planId) => {
    const plan = displayedPlans.find(p => p.id === planId);
    if (plan) {
      setViewingPlan(plan);
      setShowPlanViewer(true);
    }
  };

  const handlePlanEditorClose = () => {
    setShowPlanEditor(false);
    setEditingPlanId(null);
  };

  const handlePlanEditorSave = (result) => {
    // Refresh data after successful save
    fetchOverviewData();
    if (activeTab === 'plans') {
      fetchPlansData();
    }
  };

  const handleShowVersionHistory = (planId, planName) => {
    setVersionHistoryPlanId({ id: planId, name: planName });
    setShowVersionHistory(true);
  };

  const handleShowComparison = (planId, version1, version2) => {
    setComparisonData({ planId, version1, version2 });
    setShowVersionHistory(false);
    setShowPlanComparison(true);
  };

  const handleAdvancedSearch = async (searchParams) => {
    setIsSearching(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/plans/search`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(searchParams)
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.plans);
        toast.success(`Found ${data.total_count} plans matching your criteria`);
      } else {
        toast.error('Search failed');
      }
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchResults(null);
    setSelectedPlans([]);
  };

  const handleBulkOperationComplete = (result) => {
    // Refresh data and clear search results to show updated state
    setSearchResults(null);
    fetchPlansData();
    fetchOverviewData();
  };

  const displayedPlans = searchResults || plans;

  const fetchOverviewData = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/overview`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setOverview(data);
        
        // Extract health and performance data if available
        if (data.health) {
          setSystemHealth(data.health);
        }
        if (data.performance) {
          setPerformanceMetrics(data.performance);
        }
        
        setLastUpdated(new Date(data.last_updated || Date.now()));
      } else {
        const errorText = await response.text();
        console.error('Overview fetch failed:', response.status, errorText);
        setError('Failed to load monetization overview');
      }
    } catch (err) {
      console.error('Error fetching overview:', err);
      setError('Failed to load monetization information. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlansData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/plans`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      } else {
        setError('Failed to load plans data');
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load plans information');
    }
  };

  const fetchApprovalsData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/approvals`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setApprovals(data.approval_requests || []);
      } else {
        setError('Failed to load approvals data');
      }
    } catch (err) {
      console.error('Error fetching approvals:', err);
      setError('Failed to load approvals information');
    }
  };

  const fetchSyncData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/stripe-sync`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);
      } else {
        setError('Failed to load sync data');
      }
    } catch (err) {
      console.error('Error fetching sync data:', err);
      setError('Failed to load sync information');
    }
  };

  const getSyncStatusColor = (status) => {
    switch (status) {
      case 'synced': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      case 'unsynced': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20';
      case 'error': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20';
      case 'syncing': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
    }
  };

  const getApprovalStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      case 'pending': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20';
      case 'rejected': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20';
      case 'draft': return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
    }
  };

  if (loading && activeTab === 'overview') {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading monetization studio...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'plans', label: 'Plan Management', icon: Package },
    { id: 'features', label: 'Feature Matrix', icon: Grid3X3 },
    { id: 'pricing', label: 'Pricing Controls', icon: PricingIcon },
    { id: 'approvals', label: 'Approvals', icon: Shield },
    { id: 'sync', label: 'Stripe Sync', icon: Sync },
    { id: 'wallet', label: 'Wallet Management', icon: Wallet }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Monetization Studio</h2>
              <p className="text-gray-600 dark:text-gray-300">Enterprise-grade monetization management with governance controls</p>
            </div>
            
            {/* Real-time status indicators */}
            <div className="hidden lg:block">
              {/* Simplified status display - don't show connection status unless connected */}
              {systemHealth ? (
                <div className="flex items-center gap-4">
                  {/* Overall system status */}
                  <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/20">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                      System Operational
                    </span>
                  </div>
                  
                  {/* Individual component status */}
                  <div className="flex items-center gap-2">
                    {Object.entries(systemHealth).map(([component, status]) => (
                      <div key={component} className="flex items-center gap-1 px-2 py-1 rounded bg-gray-100 dark:bg-gray-800">
                        {status.status === 'healthy' ? (
                          <CheckCircle className="w-3 h-3 text-green-600" />
                        ) : status.status === 'degraded' ? (
                          <AlertCircle className="w-3 h-3 text-yellow-600" />
                        ) : (
                          <AlertCircle className="w-3 h-3 text-red-600" />
                        )}
                        <span className="text-xs font-medium capitalize text-gray-600 dark:text-gray-300">
                          {component}
                        </span>
                        {status.response_time_ms && (
                          <span className="text-xs text-gray-400">
                            {status.response_time_ms}ms
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    System Active
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {lastUpdated && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Last updated: {lastUpdated.toLocaleString()}
              {autoRefreshEnabled ? (
                <span className="ml-2 text-green-600">● Auto-refresh</span>
              ) : (
                <span className="ml-2 text-gray-400">● Manual</span>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => {
              fetchOverviewData();
              fetchSystemAlerts();
            }} 
            variant="outline" 
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          
          <Button
            onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
            variant={autoRefreshEnabled ? "default" : "outline"}
            size="sm"
          >
            {autoRefreshEnabled ? (
              <>
                <Clock className="w-4 h-4 mr-2" />
                Auto-refresh On
              </>
            ) : (
              <>
                <Clock className="w-4 h-4 mr-2" />
                Auto-refresh Off
              </>
            )}
          </Button>
        </div>
      </div>

      {/* System Alerts - only show critical ones and limit count */}
      {alerts.filter(alert => alert.type === 'critical').slice(0, 2).length > 0 && (
        <AlertManager 
          alerts={alerts.filter(alert => alert.type === 'critical').slice(0, 2)} 
          onResolveAlert={handleResolveAlert} 
        />
      )}

      {/* System status for mobile - simplified */}
      <div className="lg:hidden">
        <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">System Status</span>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-600">Operational</span>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && overview && (
            <div className="space-y-6">
              {/* Performance Metrics */}
              {performanceMetrics && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">System Performance</h4>
                  <PerformanceMetrics metrics={performanceMetrics} />
                </div>
              )}
              
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
                  <div className="flex items-center">
                    <Package className="w-8 h-8 text-blue-600" />
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Total Plans</div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{overview.stats.total_plans}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
                  <div className="flex items-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Active Plans</div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{overview.stats.active_plans}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
                  <div className="flex items-center">
                    <Clock className="w-8 h-8 text-yellow-600" />
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Pending Approvals</div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{overview.stats.pending_approvals}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
                  <div className="flex items-center">
                    <Sync className="w-8 h-8 text-orange-600" />
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Stripe Sync Pending</div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{overview.stats.stripe_sync_pending}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Activity</h3>
                </div>
                
                <div className="p-6">
                  {overview.recent_activity?.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-sm font-medium text-gray-900 mb-1 dark:text-gray-100">No recent activity</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Monetization changes will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {overview.recent_activity?.map((activity, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                          <div className="flex-shrink-0">
                            <GitBranch className="w-5 h-5 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {activity.actor_name} {activity.action} {activity.object_type}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {activity.module} • {new Date(activity.timestamp).toLocaleString()}
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {activity.success ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Plans Management Tab */}
          {activeTab === 'plans' && (
            <div className="space-y-6">
              {/* Advanced Search */}
              <AdvancedPlanSearch 
                onSearch={handleAdvancedSearch}
                onClear={handleClearSearch}
                isSearching={isSearching}
              />

              {/* Bulk Operations */}
              <BulkPlanOperations
                selectedPlans={selectedPlans}
                onSelectionChange={setSelectedPlans}
                onOperationComplete={handleBulkOperationComplete}
                plans={displayedPlans}
              />

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Plan Management</h3>
                  {searchResults && (
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Showing {searchResults.length} of {plans.length} plans
                    </p>
                  )}
                </div>
                <Button size="sm" onClick={handleCreatePlan}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Plan
                </Button>
              </div>

              {displayedPlans.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-gray-900 mb-1 dark:text-gray-100">
                    {searchResults ? 'No plans match your criteria' : 'No plans found'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {searchResults ? 'Try adjusting your search filters' : 'Create your first pricing plan to get started'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-12">
                          <input
                            type="checkbox"
                            checked={selectedPlans.length === displayedPlans.length && displayedPlans.length > 0}
                            onChange={() => {
                              if (selectedPlans.length === displayedPlans.length) {
                                setSelectedPlans([]);
                              } else {
                                setSelectedPlans(displayedPlans.map(plan => plan.id));
                              }
                            }}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Plan
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Pricing
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Version
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Stripe Sync
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700">
                      {displayedPlans.map((plan) => (
                        <tr key={plan.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedPlans.includes(plan.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedPlans(prev => [...prev, plan.id]);
                                } else {
                                  setSelectedPlans(prev => prev.filter(id => id !== plan.id));
                                }
                              }}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {plan.name}
                                {plan.is_most_popular && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
                                    Most Popular
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{plan.key}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-gray-100">
                              {plan.current_version ? (
                                <>
                                  <div className="font-medium">
                                    ${plan.current_version.pricing?.monthly || 0}/month
                                  </div>
                                  <div className="text-gray-500 dark:text-gray-400">
                                    ${plan.current_version.pricing?.annual || 0}/year
                                  </div>
                                </>
                              ) : (
                                <span className="text-gray-400">No pricing set</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              plan.is_public ? 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20' : 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20'
                            }`}>
                              {plan.is_public ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                              {plan.is_public ? 'Public' : 'Draft'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                v{plan.current_version?.version || 1}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleShowVersionHistory(plan.id, plan.name)}
                              >
                                <GitBranch className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              getSyncStatusColor(plan.stripe_sync?.sync_status || 'unsynced')
                            }`}>
                              {plan.stripe_sync?.sync_status === 'synced' && <CheckCircle className="w-3 h-3" />}
                              {plan.stripe_sync?.sync_status === 'syncing' && <Loader2 className="w-3 h-3 animate-spin" />}
                              {plan.stripe_sync?.sync_status === 'error' && <AlertTriangle className="w-3 h-3" />}
                              {!plan.stripe_sync?.sync_status && <Clock className="w-3 h-3" />}
                              {plan.stripe_sync?.sync_status || 'Unsynced'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => handleViewPlan(plan.id)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleEditPlan(plan.id)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Feature Matrix Tab */}
          {activeTab === 'features' && (
            <FeatureMatrix />
          )}

          {/* Pricing Controls Tab */}
          {activeTab === 'pricing' && (
            <PricingControls />
          )}

          {/* Approvals Tab */}
          {activeTab === 'approvals' && (
            <EnhancedApprovals />
          )}

          {/* Stripe Sync Tab */}
          {activeTab === 'sync' && (
            <EnhancedStripeSync />
          )}

          {/* Wallet Management Tab */}
          {activeTab === 'wallet' && (
            <EnhancedWalletManagement />
          )}
        </div>
      </div>

      {/* Plan Editor Modal */}
      {showPlanEditor && (
        <PlanEditor
          planId={editingPlanId}
          onClose={handlePlanEditorClose}
          onSave={handlePlanEditorSave}
        />
      )}

      {/* Plan Viewer Modal */}
      {showPlanViewer && viewingPlan && (
        <PlanViewer
          plan={viewingPlan}
          onClose={() => setShowPlanViewer(false)}
          onEdit={(planId) => {
            setShowPlanViewer(false);
            handleEditPlan(planId);
          }}
          onViewHistory={(planId, planName) => {
            setShowPlanViewer(false);
            handleShowVersionHistory(planId, planName);
          }}
        />
      )}

      {/* Plan Version History Modal */}
      {showVersionHistory && versionHistoryPlanId && (
        <PlanVersionHistory
          planId={versionHistoryPlanId.id}
          planName={versionHistoryPlanId.name}
          onClose={() => setShowVersionHistory(false)}
          onCompare={handleShowComparison}
        />
      )}

      {/* Plan Comparison Modal */}
      {showPlanComparison && comparisonData && (
        <PlanComparison
          planId={comparisonData.planId}
          version1={comparisonData.version1}
          version2={comparisonData.version2}
          onClose={() => setShowPlanComparison(false)}
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
};

export default MonetizationStudio;